import { useToast } from '@ui';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { useSaveCustomer, type Customer } from '../api';
import { CustomerFields, customerErrors, useCustomerForm } from './CustomerFields';

export interface CustomerFormDialogProps {
  open: boolean;
  onClose: () => void;
  customer?: Customer;
  onSaved?: (customer: Customer) => void;
}

/**
 * Creates or edits a customer (CUS-001): salutation, first and last name of
 * a person, or the company name with an optional contact person.
 */
export function CustomerFormDialog({ open, onClose, customer, onSaved }: CustomerFormDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const save = useSaveCustomer();
  const form = useCustomerForm(customer);
  const errors = customerErrors(save.error);
  const close = () => {
    save.reset();
    onClose();
  };
  const submit = () =>
    save.mutate(
      { id: customer?.id, version: customer?.version, body: form.body() },
      {
        onSuccess: (c) => {
          toast.show(customer ? t('crm.form.saved') : t('crm.form.created', { name: c.name }));
          onSaved?.(c);
          close();
        },
      },
    );

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={customer ? t('crm.form.editTitle') : t('crm.form.newTitle')}
      submitLabel={t('common.save')}
      onSubmit={submit}
      pending={save.isPending}
      error={errors.general}
    >
      <CustomerFields form={form} errors={errors} />
    </FormDialog>
  );
}
